// ===========================================
// TABLE SETTINGS STORE
// ===========================================
// Manages player-specific audio settings for table play
// Persisted to localStorage
//
// NOTE: Music feature temporarily disabled - see BACKLOG.md
// Music settings are retained for future re-enablement

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface TableSettings {
  // Music settings (DISABLED - see BACKLOG.md)
  musicEnabled: boolean;
  musicVolume: number; // 0-100 (UI slider percentage)
  musicPreset: string; // e.g., "Custom 1"

  // Sound effects settings
  sfxVolume: number; // 0-100 (cards, chips, etc.)
  sfxMuted: boolean; // Mute cards & chips SFX
  outcomeVolume: number; // 0-100 (hand outcome SFX)
  outcomeMuted: boolean; // Mute hand outcome SFX
}

interface TableSettingsState extends TableSettings {
  // Actions
  setMusicEnabled: (enabled: boolean) => void;
  setMusicVolume: (volume: number) => void;
  setMusicPreset: (preset: string) => void;
  setSfxVolume: (volume: number) => void;
  setSfxMuted: (muted: boolean) => void;
  setOutcomeVolume: (volume: number) => void;
  setOutcomeMuted: (muted: boolean) => void;
  resetToDefaults: () => void;
}

// Default music volume maps to -35 dB in Tone.js
// Slider 0% = -60 dB (near silent), 100% = 0 dB (full), 50% = -30 dB
// We want default at ~42% to get -35 dB
const DEFAULT_MUSIC_VOLUME = 42; // Maps to approximately -35 dB

const DEFAULT_SETTINGS: TableSettings = {
  musicEnabled: false, // DISABLED - see BACKLOG.md
  musicVolume: DEFAULT_MUSIC_VOLUME,
  musicPreset: 'Custom 1',
  sfxVolume: 50,
  sfxMuted: false,
  outcomeVolume: 50,
  outcomeMuted: false,
};

export const useTableSettingsStore = create<TableSettingsState>()(
  persist(
    (set) => ({
      ...DEFAULT_SETTINGS,

      setMusicEnabled: (enabled) => set({ musicEnabled: enabled }),

      setMusicVolume: (volume) => set({ musicVolume: Math.max(0, Math.min(100, volume)) }),

      setMusicPreset: (preset) => set({ musicPreset: preset }),

      setSfxVolume: (volume) => set({ sfxVolume: Math.max(0, Math.min(100, volume)) }),

      setSfxMuted: (muted) => set({ sfxMuted: muted }),

      setOutcomeVolume: (volume) => set({ outcomeVolume: Math.max(0, Math.min(100, volume)) }),

      setOutcomeMuted: (muted) => set({ outcomeMuted: muted }),

      resetToDefaults: () => set(DEFAULT_SETTINGS),
    }),
    {
      name: 'table-settings',
    }
  )
);

// Helper to get SFX volume as 0-1 for Audio elements (respects mute)
export const getSfxVolumeNormalized = () => {
  const state = useTableSettingsStore.getState();
  return state.sfxMuted ? 0 : state.sfxVolume / 100;
};

// Helper to get outcome SFX volume as 0-1 for Audio elements (respects mute)
export const getOutcomeVolumeNormalized = () => {
  const state = useTableSettingsStore.getState();
  return state.outcomeMuted ? 0 : state.outcomeVolume / 100;
};

// Helper to get music volume as 0-1
export const getMusicVolumeNormalized = () => {
  return useTableSettingsStore.getState().musicVolume / 100;
};

// Convert slider percentage (0-100) to Tone.js dB (-60 to 0)
// 0% = -60 dB (near silent), 100% = 0 dB (full volume)
export const getMusicVolumeDb = () => {
  const percent = useTableSettingsStore.getState().musicVolume;
  // Linear mapping: 0% -> -60dB, 100% -> 0dB
  return -60 + (percent / 100) * 60;
};
